import type { ReportRenderContext } from "../types/reportRenderContext";
import { reportTemplateUsesOrganMaps } from "../utils/reportOrganMapSpecimens";
import { getReportPreview, type ReportPreviewData } from "./signOff.service";

type PreviewSpecimen = NonNullable<ReportPreviewData["specimens"]>[number];
const MAP_UNAVAILABLE = "The signed report map image is unavailable.";

function isEmbeddedMap(value: string | null): boolean {
  return (
    typeof value === "string" &&
    /^data:image\/[a-z0-9.+-]+;base64,[a-z0-9+/]+=*$/i.test(value)
  );
}

function needsMapAsset(specimen: PreviewSpecimen): boolean {
  return (
    Boolean(specimen.organMapTemplateId) &&
    !isEmbeddedMap(specimen.organMapImageUrl)
  );
}

function requireSignedTemplate(context: ReportRenderContext): string {
  const templateId = context.reportRenderSnapshot?.reportData?.template.id;
  if (
    !context.signOffId ||
    !Number.isInteger(context.reportVersion) ||
    context.reportVersion < 1 ||
    !context.templateId ||
    templateId !== context.templateId
  )
    throw new Error(MAP_UNAVAILABLE);
  return context.templateId;
}

function requireSpecimenIdentity(
  frozen: PreviewSpecimen,
  specimen: PreviewSpecimen
): void {
  if (
    specimen.number !== frozen.number ||
    specimen.organMapTemplateId !== frozen.organMapTemplateId ||
    specimen.organMapWidth !== frozen.organMapWidth ||
    specimen.organMapHeight !== frozen.organMapHeight
  )
    throw new Error(MAP_UNAVAILABLE);
}

function matchingSignedSpecimen(
  frozen: PreviewSpecimen,
  signed: ReportPreviewData
): PreviewSpecimen {
  const matches =
    signed.specimens?.filter((specimen) => specimen.id === frozen.id) || [];
  if (matches.length !== 1) throw new Error(MAP_UNAVAILABLE);
  requireSpecimenIdentity(frozen, matches[0]);
  return matches[0];
}

function requireMapKeyAuthority(
  frozen: PreviewSpecimen,
  signed: ReportPreviewData
): void {
  const authorities =
    signed.organMaps?.filter((map) => map.id === frozen.organMapTemplateId) ||
    [];
  if (
    !frozen.organMapImageUrl ||
    authorities.length !== 1 ||
    authorities[0].imageUrl !== frozen.organMapImageUrl
  )
    throw new Error(MAP_UNAVAILABLE);
}

function hydrateSpecimen(
  frozen: PreviewSpecimen,
  signed: ReportPreviewData
): PreviewSpecimen {
  if (!needsMapAsset(frozen)) return frozen;
  const specimen = matchingSignedSpecimen(frozen, signed);
  requireMapKeyAuthority(frozen, signed);
  if (!isEmbeddedMap(specimen.organMapImageUrl))
    throw new Error(MAP_UNAVAILABLE);
  return { ...frozen, organMapImageUrl: specimen.organMapImageUrl };
}

async function loadSignedAssets(
  context: ReportRenderContext
): Promise<ReportPreviewData> {
  const templateId = requireSignedTemplate(context);
  const signed = await getReportPreview(
    context.caseId,
    context.reportVersion,
    templateId
  );
  requirePreviewTemplate(signed, templateId, context);
  return signed;
}

function requirePreviewTemplate(
  signed: ReportPreviewData,
  templateId: string,
  context: ReportRenderContext
): void {
  const frozenTemplateId =
    context.reportRenderSnapshot?.supplementalData?.previewData?.templateId;
  if (
    signed.templateId !== templateId ||
    (frozenTemplateId && frozenTemplateId !== templateId)
  )
    throw new Error(MAP_UNAVAILABLE);
}

function contextWithPreview(
  context: ReportRenderContext,
  previewData: ReportPreviewData
): ReportRenderContext {
  const snapshot = context.reportRenderSnapshot;
  return {
    ...context,
    reportRenderSnapshot: {
      ...snapshot,
      supplementalData: { ...snapshot?.supplementalData, previewData },
    },
  };
}

function shouldHydrateSignedMapAssets(context: ReportRenderContext): boolean {
  const reportData = context.reportRenderSnapshot?.reportData;
  const sections = reportData?.template.templateContent.sections;
  return (
    !context.reconstructionId &&
    !reportData?.legacyBaseline &&
    (!sections || reportTemplateUsesOrganMaps(sections))
  );
}

/** Resolve only asset presentation; signed clinical facts remain the frozen snapshot's authority. */
export async function hydrateReportRenderMapAssets(
  context: ReportRenderContext
): Promise<ReportRenderContext> {
  if (!shouldHydrateSignedMapAssets(context)) return context;
  const preview = context.reportRenderSnapshot?.supplementalData?.previewData;
  if (!preview?.specimens?.some(needsMapAsset)) return context;
  const signed = await loadSignedAssets(context);
  return contextWithPreview(context, {
    ...preview,
    specimens: preview.specimens.map((specimen) =>
      hydrateSpecimen(specimen, signed)
    ),
  });
}
